import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE } from '../i18n/constants';
import { getRequest } from '../services/api';
import type { SignInPlatform } from '../services/types';
import { getParam } from '../utils/marketingHelper';

import { useQuery } from './queries/useCustomQuery';

interface BannerImage {
  language: string;
  imageUrl: string;
}

export interface UserPreview {
  displayName: string | null;
  email: string | null;
  providers: SignInPlatform[];
}

interface LanderBannerResponse {
  data: {
    bannerImages: BannerImage[];
    benefitType: string | null;
    userPreview: UserPreview | null;
    success: boolean;
    message: string;
  };
}

const getQueryParams = (type: 'marketing' | 'social') => {
  const params = new URLSearchParams(window.location.search);
  const utmPromo = getParam(params, 'utm_promo');
  const pc = getParam(params, 'pc');
  const utmFeature = getParam(params, 'utm_feature');
  const fuid = params.get('fuid');

  const queryParams: Record<string, string> = { type };
  if (utmPromo) queryParams.utm_promo = utmPromo;
  if (pc) queryParams.pc = pc;
  if (utmFeature) queryParams.utm_feature = utmFeature;
  if (fuid) queryParams.fuid = fuid;

  return { queryParams, utmPromo, pc, utmFeature, fuid };
};

export const useLanderBanner = (type: 'marketing' | 'social', { enabled = true }: { enabled?: boolean } = {}) => {
  const { i18n } = useTranslation();
  const { queryParams, utmPromo, pc, utmFeature, fuid } = getQueryParams(type);

  const { data, isLoading } = useQuery<
    LanderBannerResponse,
    Error,
    { bannerImages: BannerImage[]; benefitType: string | null; userPreview: UserPreview | null }
  >({
    queryKey: ['landerBanner', type, utmPromo, pc, utmFeature, fuid],
    queryFn: () => getRequest<LanderBannerResponse>('landing/banner', queryParams),
    enabled,
    select: (res) => ({
      bannerImages: res?.data?.bannerImages || [],
      benefitType: res?.data?.benefitType || null,
      userPreview: res?.data?.userPreview || null,
    }),
  });

  const userPreview = data?.userPreview || null;
  const benefitType = data?.benefitType || null;

  const bannerUrl = useMemo(() => {
    const bannerImages = data?.bannerImages || [];
    if (bannerImages.length === 0) return null;

    const currentLang = (i18n.language || DEFAULT_LANGUAGE).toUpperCase().slice(0, 2);
    const langImage = bannerImages.find((img) => img.language === currentLang);
    const enImage = bannerImages.find((img) => img.language === DEFAULT_LANGUAGE);
    const selectedImage = langImage || enImage || bannerImages[0];
    return selectedImage?.imageUrl || null;
  }, [data?.bannerImages, i18n.language]);

  return { bannerUrl, benefitType, userPreview, isLoading };
};
